import { ContractState } from "../types/contractState.types";
import { ServiceContract, Milestone } from "../types/service.type";
import {
  MilestoneStatusEnum,
  ServiceContractStatusEnum,
  ServiceRequestStatusEnum,
  ServiceRequestTypeEnum,
} from "../enum/service.enum";

/**
 * Checks if the contract payment has been completed.
 * Payment is considered complete if:
 * - Contract was canceled after payment, OR
 * - First milestone is in Pinning or Paid status AND
 *   service request is in Contracted status AND
 *   contract is in Approved status
 */
export const isContractPaid = (contractState: ContractState): boolean => {
  const { contractStatus, serviceRequestStatus, milestones } = contractState;

  // If contract was canceled after payment, it's considered paid
  if (contractStatus === ServiceContractStatusEnum.CanceledAfterPaid) {
    return true;
  }

  // Check if first milestone is paid or pinning
  const firstMilestone = milestones[0];
  const firstMilestoneIsPaidOrPinning =
    firstMilestone?.status === MilestoneStatusEnum.Paid || firstMilestone?.status === MilestoneStatusEnum.Pinning;

  // Payment is complete if first milestone is paid/pinning AND
  // service request is contracted AND contract is approved
  return (
    firstMilestoneIsPaidOrPinning &&
    serviceRequestStatus === ServiceRequestStatusEnum.Contracted &&
    contractStatus === ServiceContractStatusEnum.Approved
  );
};

/**
 * Checks if contract payment is complete based on payment data status or milestones.
 * This is a helper that combines paymentData status check with milestone status.
 */
export const isContractPaidWithPaymentData = (contractState: ContractState, hasPaidMilestone: boolean): boolean => {
  const paymentSuccess = contractState.paymentData?.status === "success";
  // Also check if first milestone is Pinning (which indicates payment was made)
  const firstMilestone = contractState.milestones[0];
  const firstMilestoneIsPinning = firstMilestone?.status === MilestoneStatusEnum.Pinning;
  return paymentSuccess || hasPaidMilestone || firstMilestoneIsPinning;
};

/**
 * Checks if any milestone has been paid.
 */
export const hasPaidMilestone = (milestones: Milestone[]): boolean => {
  return milestones.some((milestone) => milestone.status === MilestoneStatusEnum.Paid);
};

/**
 * Checks if the contract is in a contracted or approved state.
 */
export const isContractContracted = (contractStatus: ServiceContractStatusEnum): boolean => {
  return (
    contractStatus === ServiceContractStatusEnum.Contracted || contractStatus === ServiceContractStatusEnum.Approved
  );
};

/**
 * Determines if a requester can proceed to payment.
 * Conditions:
 * - Contract is not paid yet
 * - Contract is in contracted/approved state
 * - User is NOT the provider (i.e., is the requester)
 */
export const canContinueToPayment = (contractState: ContractState, isProvider: boolean): boolean => {
  if (isProvider) {
    return false;
  }

  const contractPaid = isContractPaid(contractState);
  const contracted = isContractContracted(contractState.contractStatus);

  return !contractPaid && contracted;
};

/**
 * Determines if a user can review a service request.
 * Conditions:
 * - Service request is canceled AND type is ordered/received AND first milestone is pending/paid, OR
 * - Service request is completed
 */
export const canReviewRequest = (contract: ServiceContract): boolean => {
  const { serviceRequest, milestones } = contract;

  const isFirstPending = (milestones: Milestone[]) => milestones[0]?.status === MilestoneStatusEnum.Pinning;

  const isFirstPaid = (milestones: Milestone[]) => milestones[0]?.status === MilestoneStatusEnum.Paid;

  const isCanceled = serviceRequest.status === ServiceRequestStatusEnum.Canceled;
  const isOrderedOrReceived =
    serviceRequest.type === ServiceRequestTypeEnum.ordered || serviceRequest.type === ServiceRequestTypeEnum.received;
  const firstIsPendingOrPaid = isFirstPending(milestones) || isFirstPaid(milestones);

  const isServiceCompleted = serviceRequest.status === ServiceRequestStatusEnum.Completed;

  return (isCanceled && isOrderedOrReceived && firstIsPendingOrPaid) || isServiceCompleted;
};
